import { apiRequest } from '../api';
import { canGoBackInApp, navigateTo } from '../router';
import { state } from '../state';
import { getLocale, t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { STAT_ORDER } from '../radar';
import { ARCHETYPE_ICONS, buildArchetypeBlock } from '../archetype';
import { openGameDetailModal } from '../ui/player-detail';
import { buildChallengeCard, type ChallengeResponse } from '../ui/challenge';
import type { ArchetypeSignal, PlayerTilt } from '../types';

// ============================================================
// Личная статистика (/my-stats): сводка по своему игроку,
// график MMR, факты и советы по улучшению игры.
// ============================================================

interface BucketRow {
  key: string;
  name: string;
  games: number;
  wins: number;
  winrate: number;
  avg_place: number | null;
}

interface Note {
  id: string;
  params: Record<string, string | number>;
}

interface MyStatsSummary {
  elo: number;
  rank: number | null;
  total_players: number;
  rank_percent: number | null;
  games: number;
  wins: number;
  winrate: number;
  avg_place: number | null;
  activity: number;
  game_days: number;
  solo_games: number;
  team_games: number;
  peak_elo: number | null;
  elo_30d: number;
  games_30d: number;
  best_streak: number;
  current_win_streak: number;
  current_loss_streak: number;
  sample_games: number;
}

interface ArchetypeDistItem {
  id: string;
  count: number;
  percent: number;
}

interface MyStatsResponse {
  linked: true;
  admin_view?: boolean;
  player: { id: number; name: string; aka: string | null };
  summary: MyStatsSummary;
  form: Array<{ won: boolean; game_id: number }>;
  elo_points: Array<{ t: number; elo: number }>;
  heatmap: Array<{ day: number; hour: number; games: number; wins: number }>;
  percentiles: Array<{ key: string; value: number | null; percentile: number | null }>;
  achievements: Array<{ id: string; current: number; target: number; unlocked: boolean }>;
  style: {
    mine: Record<string, number>;
    league: Record<string, number>;
    votes: number;
  } | null;
  archetype: {
    id: string;
    match: number;
    modifier: string | null;
    axes: { aggression: number; greed: number; turtle: number };
    traits: Array<{ id: string; params: Record<string, string | number> }>;
    games: number;
    votes: number;
    near?: { id: string; match: number } | null;
    signals?: {
      aggression: ArchetypeSignal;
      greed: ArchetypeSignal;
      turtle: ArchetypeSignal;
    };
    tilt?: PlayerTilt | null;
  } | null;
  archetype_distribution: {
    total: number;
    items: ArchetypeDistItem[];
  } | null;
  survival: {
    avg_share: number | null;
    top2_rate: number | null;
    avg_win_min: number | null;
    durations: Array<{ key: string; count: number }>;
  };
  records: {
    longest_break_days: number;
    longest_break_from: string | null;
    longest_break_to: string | null;
    max_games_day: { date: string; count: number } | null;
    map_streak_name: string | null;
    map_streak_count: number;
  };
  wrapped: {
    month: string;
    games: number;
    wins: number;
    winrate: number;
    elo_delta: number;
    favorite_map: string | null;
    top_rival: string | null;
    best_day: { date: string; count: number } | null;
    active_days: number;
    fastest_win: number | null;
    longest_win: number | null;
  } | null;
  lobby_sizes: BucketRow[];
  rival_edges: { best: BucketRow | null; worst: BucketRow | null };
  timeline: Array<{
    game_id: number;
    played_at: string;
    won: boolean;
    place: number | null;
    player_count: number;
    map_name: string | null;
    race: string;
    elo_delta: number | null;
  }>;
  races: BucketRow[];
  places: Array<{ place: number; count: number }>;
  maps: BucketRow[];
  formats: BucketRow[];
  mods: BucketRow[];
  rivals: BucketRow[];
  recent: Array<{
    game_id: number;
    played_at: string;
    map_name: string | null;
    won: boolean;
    place: number | null;
    player_count: number;
    duration_min: number | null;
    race: string;
    elo_delta: number | null;
  }>;
  facts: Note[];
  advice: Note[];
}

type MyStatsApiResponse = MyStatsResponse | { linked: false; admin_view?: boolean };

const NOTE_ICONS: Record<string, string> = {
  rank: '🏅',
  peak: '📈',
  favorite_race: '⚔️',
  best_map: '🗺️',
  most_played_map: '🧭',
  favorite_format: '🎮',
  favorite_mod: '🧩',
  best_day: '📅',
  fastest_win: '⚡',
  longest_win: '⏳',
  rival: '🤝',
  nemesis: '😤',
  victim: '🎯',
  best_streak: '🔥',
  activity: '🗓️',
  avg_place: '📊',
  favorite_hour: '🕒',
  win_streak_now: '🔥',
  loss_streak_now: '💤',
  sample: '🧮',
  play_more: '🎮',
  low_winrate: '📉',
  race_weak: '🧬',
  map_avoid: '🚫',
  avg_place_high: '🏁',
  early_elim: '🛡️',
  losing_streak: '💤',
  winning_streak: '🔥',
  low_activity: '📅',
  close_to_rank: '🎯',
  map_hidden_gem: '💎',
  balanced: '✅',
};

const RACE_CLASS: Record<string, string> = {
  T: 'is-terran',
  Z: 'is-zerg',
  P: 'is-protoss',
  R: 'is-random',
};

const ACH_ICONS: Record<string, string> = {
  first_win: '🏆',
  games_10: '🎮',
  games_50: '🎯',
  games_100: '💯',
  streak_5: '🔥',
  streak_10: '⚡',
  winrate_55: '📈',
  big_lobby: '🐘',
  map_master: '🗺️',
  survivor: '🛡️',
  night_owl: '🌙',
  all_races: '🧬',
  veteran: '🗓️',
};

let active = false;
let lastData: MyStatsApiResponse | null = null;
let lastChallenge: ChallengeResponse | null = null;
let localeListener: (() => void) | null = null;
// Админ-просмотр чужой статистики: /my-stats?user_id=<uuid>.
let viewedUserId: string | null = null;
// Публичный просмотр статистики игрока с карточки: /my-stats?player_id=<id>.
let viewedPlayerId: string | null = null;

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

function fmtNumber(value: number): string {
  return Math.round(value).toLocaleString(getLocale() === 'ru' ? 'ru-RU' : 'en-US');
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(getLocale() === 'ru' ? 'ru-RU' : 'en-GB', {
    day: '2-digit',
    month: 'short',
  });
}

/** Дата из ключа YYYY-MM-DD — полдень, чтобы не съехать на часовом поясе. */
function fmtDateKey(key: string | null): string {
  if (!key) return '—';
  return fmtDate(`${key}T12:00:00`);
}

function buildStatTile(label: string, value: string, hint?: string): HTMLElement {
  const tile = node('div', 'mystats-tile');
  tile.append(node('span', 'mystats-tile-label', label), node('span', 'mystats-tile-value', value));
  if (hint) tile.appendChild(node('span', 'mystats-tile-hint', hint));
  return tile;
}

/** Карточки фактов/советов: id переводим через i18n, params подставляем. */
function buildNotes(notes: Note[], prefix: 'fact' | 'advice'): HTMLElement {
  const list = node('div', prefix === 'fact' ? 'mystats-facts' : 'mystats-advice');
  for (const note of notes) {
    const card = node('div', `mystats-note mystats-note--${prefix}`);
    const icon = node('span', 'mystats-note-icon', NOTE_ICONS[note.id] ?? '•');
    // День недели сервер отдаёт числом (1=Пн) — переводим в подпись.
    const params = { ...note.params };
    if (note.id === 'best_day' && typeof params.day === 'number') {
      params.day = t(`mystats.day.${params.day}` as TranslationKey);
    }
    const text = t(`mystats.${prefix}.${note.id}` as TranslationKey, params);
    card.append(icon, node('span', 'mystats-note-text', text));
    list.appendChild(card);
  }
  if (notes.length === 0) {
    list.appendChild(node('p', 'hint', t('mystats.no_data')));
  }
  return list;
}

/** Мини-график MMR: линия и заливка, без осей — только подписи начала/конца. */
function buildEloChart(points: Array<{ t: number; elo: number }>): HTMLElement {
  const wrap = node('div', 'mystats-chart');
  if (points.length < 2) {
    wrap.appendChild(node('p', 'hint', t('mystats.no_data')));
    return wrap;
  }

  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) {
    wrap.appendChild(node('p', 'hint', t('mystats.no_data')));
    return wrap;
  }

  const W = 640;
  const H = 170;
  const PAD = 12;
  const elos = points.map((p) => p.elo);
  const min = Math.min(...elos);
  const max = Math.max(...elos);
  const range = max - min || 1;
  const x = (i: number) => PAD + (i / (points.length - 1)) * (W - PAD * 2);
  const y = (elo: number) => H - PAD - ((elo - min) / range) * (H - PAD * 2);

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.elo).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${H - PAD} L${x(0).toFixed(1)},${H - PAD} Z`;

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.classList.add('mystats-chart-svg');

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
  gradient.setAttribute('id', 'mystats-elo-gradient');
  gradient.setAttribute('x1', '0');
  gradient.setAttribute('y1', '0');
  gradient.setAttribute('x2', '0');
  gradient.setAttribute('y2', '1');
  const stopTop = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stopTop.setAttribute('offset', '0%');
  stopTop.setAttribute('stop-color', '#f1c40f');
  stopTop.setAttribute('stop-opacity', '0.42');
  const stopBottom = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stopBottom.setAttribute('offset', '100%');
  stopBottom.setAttribute('stop-color', '#f1c40f');
  stopBottom.setAttribute('stop-opacity', '0');
  gradient.append(stopTop, stopBottom);
  defs.appendChild(gradient);

  const areaPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  areaPath.setAttribute('d', area);
  areaPath.setAttribute('fill', 'url(#mystats-elo-gradient)');

  const linePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  linePath.setAttribute('d', line);
  linePath.setAttribute('fill', 'none');
  linePath.setAttribute('stroke', '#f1c40f');
  linePath.setAttribute('stroke-width', '2');
  linePath.setAttribute('stroke-linejoin', 'round');
  linePath.setAttribute('stroke-linecap', 'round');
  linePath.setAttribute('vector-effect', 'non-scaling-stroke');

  svg.append(defs, areaPath, linePath);
  wrap.appendChild(svg);

  const legend = node('div', 'mystats-chart-legend');
  legend.append(
    node('span', '', fmtDate(new Date(first.t).toISOString())),
    node('span', 'mystats-chart-peak', `${fmtNumber(min)} – ${fmtNumber(max)}`),
    node('span', '', fmtDate(new Date(last.t).toISOString())),
  );
  wrap.appendChild(legend);
  return wrap;
}

/** Часы в порядке отображения теплокарты: с 12:00 до 11:00 (полночь в середине). */
const HEATMAP_HOURS = Array.from({ length: 24 }, (_, i) => (i + 12) % 24);
const DAY_SHORT_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const DAY_SHORT_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Теплокарта «день недели × час»: насыщенность — число игр, цвет — винрейт. */
function buildHeatmap(
  rows: Array<{ day: number; hour: number; games: number; wins: number }>,
): HTMLElement {
  const wrap = node('div', 'mystats-heatmap-wrap');
  const scroll = node('div', 'mystats-heatmap-scroll');
  const grid = node('div', 'mystats-heatmap');
  const byKey = new Map(rows.map((r) => [`${r.day}:${r.hour}`, r]));
  const maxGames = Math.max(...rows.map((r) => r.games), 1);
  const days = getLocale() === 'ru' ? DAY_SHORT_RU : DAY_SHORT_EN;

  grid.appendChild(node('span', 'mystats-heatmap-corner', ''));
  for (const hour of HEATMAP_HOURS) {
    grid.appendChild(
      node(
        'span',
        'mystats-heatmap-hour',
        hour % 3 === 0 ? String(hour).padStart(2, '0') : '',
      ),
    );
  }

  for (let day = 1; day <= 7; day += 1) {
    grid.appendChild(node('span', 'mystats-heatmap-day', days[day - 1] ?? ''));
    for (const hour of HEATMAP_HOURS) {
      const cell = node('div', 'mystats-heatmap-cell');
      const stat = byKey.get(`${day}:${hour}`);
      if (stat && stat.games > 0) {
        const winrate = Math.round((stat.wins / stat.games) * 100);
        // Красный (0%) → зелёный (100%), прозрачность — активность.
        const hue = Math.round((winrate / 100) * 120);
        const alpha = 0.2 + 0.62 * (stat.games / maxGames);
        cell.style.background = `hsla(${hue}, 68%, 42%, ${alpha.toFixed(2)})`;
        const label = `${days[day - 1]}, ${String(hour).padStart(2, '0')}:00 — ${t(
          'mystats.heatmap_cell',
          { games: stat.games, winrate },
        )}`;
        cell.title = label;
        cell.setAttribute('aria-label', label);
      }
      grid.appendChild(cell);
    }
  }

  scroll.appendChild(grid);
  wrap.appendChild(scroll);

  const legend = node('div', 'mystats-heatmap-legend');
  legend.append(
    node('span', '', t('mystats.heatmap_wr_low')),
    node('span', 'mystats-heatmap-gradient'),
    node('span', '', t('mystats.heatmap_wr_high')),
  );
  wrap.appendChild(legend);
  return wrap;
}

/** Хроника мест: высота столбика — место в лобби, клик открывает игру. */
function buildPlaceTimeline(rows: MyStatsResponse['timeline']): HTMLElement {
  const wrap = node('div', 'mystats-timeline');
  if (rows.length === 0) {
    wrap.appendChild(node('p', 'hint', t('mystats.no_data')));
    return wrap;
  }

  const strip = node('div', 'mystats-place-strip');
  for (const g of rows) {
    const col = node('button', 'mystats-place-col');
    col.type = 'button';
    const place = g.place ?? g.player_count;
    const ratio = Math.max(0.15, (g.player_count - place + 1) / g.player_count);
    const bar = node('span', 'mystats-place-bar');
    bar.style.height = `${Math.round(ratio * 100)}%`;
    bar.classList.add(
      g.won ? 'is-win' : place <= Math.ceil(g.player_count / 2) ? 'is-top' : 'is-bottom',
    );
    col.appendChild(bar);
    col.title = `${fmtDate(g.played_at)} · ${g.map_name ?? '—'} · ${t('mystats.recent_place', {
      place,
      total: g.player_count,
    })}`;
    col.setAttribute('aria-label', col.title);
    col.addEventListener('click', () => void openGameDetailModal(g.game_id));
    strip.appendChild(col);
  }
  wrap.appendChild(strip);

  const legend = node('div', 'mystats-timeline-legend');
  for (const [cls, key] of [
    ['is-win', 'mystats.timeline_win'],
    ['is-top', 'mystats.timeline_top'],
    ['is-bottom', 'mystats.timeline_bottom'],
  ] as const) {
    const item = node('span', 'mystats-timeline-legend-item');
    item.append(
      node('span', `mystats-timeline-swatch ${cls}`),
      node('span', '', t(key as TranslationKey)),
    );
    legend.appendChild(item);
  }
  wrap.appendChild(legend);
  return wrap;
}

/** Горизонтальные полосы: число игр и винрейт по расе/карте/формату. */
function buildBars(
  rows: BucketRow[],
  colored = false,
  labelFn?: (row: BucketRow) => string,
): HTMLElement {
  const list = node('div', 'mystats-bars');
  const max = Math.max(...rows.map((r) => r.games), 1);
  for (const row of rows) {
    const line = node('div', 'mystats-bar');
    const head = node('div', 'mystats-bar-head');
    const name = node(
      'span',
      `mystats-bar-name${colored ? ` mystats-race ${RACE_CLASS[row.name] ?? ''}` : ''}`,
      labelFn ? labelFn(row) : row.name,
    );
    const value = node(
      'span',
      'mystats-bar-value',
      `${row.games} · ${row.winrate}%`,
    );
    head.append(name, value);
    const track = node('div', 'mystats-bar-track');
    const fill = node('div', 'mystats-bar-fill');
    fill.style.width = `${Math.max(3, (row.games / max) * 100)}%`;
    const raceClass = RACE_CLASS[row.name];
    if (colored && raceClass) fill.classList.add(raceClass);
    track.appendChild(fill);
    line.append(head, track);
    list.appendChild(line);
  }
  return list;
}

/** Достижения: открытые — золотые, закрытые — с прогрессом. */
function buildAchievements(list: MyStatsResponse['achievements']): HTMLElement {
  const grid = node('div', 'mystats-ach-grid');
  for (const a of list) {
    const item = node('div', `mystats-ach${a.unlocked ? ' is-unlocked' : ''}`);
    const body = node('div', 'mystats-ach-body');
    body.appendChild(
      node('span', 'mystats-ach-name', t(`mystats.ach.${a.id}` as TranslationKey)),
    );
    if (a.unlocked) {
      body.appendChild(node('span', 'mystats-ach-desc', t('mystats.ach_done')));
    } else {
      const track = node('div', 'mystats-ach-track');
      const fill = node('div', 'mystats-ach-fill');
      fill.style.width = `${Math.min(100, Math.round((a.current / a.target) * 100))}%`;
      track.appendChild(fill);
      body.append(track, node('span', 'mystats-ach-progress', `${a.current} / ${a.target}`));
    }
    item.append(node('span', 'mystats-ach-icon', ACH_ICONS[a.id] ?? '🏅'), body);
    grid.appendChild(item);
  }
  return grid;
}

const PCT_LABELS: Record<string, TranslationKey> = {
  mmr: 'mystats.pct.mmr',
  winrate: 'mystats.pct.winrate',
  avg_place: 'mystats.pct.avg_place',
  activity: 'mystats.pct.activity',
  games: 'mystats.pct.games',
};

function fmtPercentileValue(key: string, value: number | null): string {
  if (value == null) return '—';
  if (key === 'winrate') return `${value}%`;
  if (key === 'avg_place') return value.toFixed(2).replace('.', ',');
  return fmtNumber(value);
}

/** Перцентили: полоса — доля игроков, которых показатель обходит. */
function buildPercentiles(rows: MyStatsResponse['percentiles']): HTMLElement {
  const list = node('div', 'mystats-pct');
  for (const row of rows) {
    const percentile = row.percentile ?? 0;
    const line = node('div', 'mystats-pct-row');
    const head = node('div', 'mystats-pct-head');
    head.append(
      node('span', 'mystats-pct-label', t(PCT_LABELS[row.key] ?? ('mystats.pct.games' as TranslationKey))),
      node('span', 'mystats-pct-value', fmtPercentileValue(row.key, row.value)),
    );
    const track = node('div', 'mystats-pct-track');
    const fill = node('div', 'mystats-pct-fill');
    fill.style.width = `${Math.max(2, percentile)}%`;
    track.appendChild(fill);
    line.append(
      head,
      track,
      node('span', 'mystats-pct-note', t('mystats.pct_better', { percent: percentile })),
    );
    list.appendChild(line);
  }
  return list;
}

/** Радар «ты против среднего по лиге»: две фигуры на одних осях. */
function buildStyleCompare(style: NonNullable<MyStatsResponse['style']>): HTMLElement {
  const wrap = node('div', 'mystats-style');
  const W = 420;
  const H = 340;
  const CX = W / 2;
  const CY = H / 2;
  const R = 112;
  const axes = STAT_ORDER;
  const n = axes.length;

  const point = (index: number, value: number): [number, number] => {
    const angle = -Math.PI / 2 + (index / n) * Math.PI * 2;
    const radius = (Math.max(0, Math.min(5, value)) / 5) * R;
    return [CX + radius * Math.cos(angle), CY + radius * Math.sin(angle)];
  };
  const polygon = (values: number[]): string =>
    values.map((v, i) => point(i, v).map((n2) => n2.toFixed(1)).join(',')).join(' ');

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.classList.add('mystats-style-svg');

  for (let level = 1; level <= 5; level += 1) {
    const ring = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    ring.setAttribute('points', polygon(Array.from({ length: n }, () => level)));
    ring.setAttribute('fill', 'none');
    ring.setAttribute('stroke', 'rgba(255, 255, 255, 0.09)');
    ring.setAttribute('stroke-width', '1');
    svg.appendChild(ring);
  }
  axes.forEach((axis, i) => {
    const [x, y] = point(i, 5);
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(CX));
    line.setAttribute('y1', String(CY));
    line.setAttribute('x2', x.toFixed(1));
    line.setAttribute('y2', y.toFixed(1));
    line.setAttribute('stroke', 'rgba(255, 255, 255, 0.09)');
    svg.appendChild(line);

    const angle = -Math.PI / 2 + (i / n) * Math.PI * 2;
    const lx = CX + (R + 30) * Math.cos(angle);
    const ly = CY + (R + 30) * Math.sin(angle);
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', lx.toFixed(1));
    label.setAttribute('y', ly.toFixed(1));
    label.setAttribute('text-anchor', Math.abs(Math.cos(angle)) < 0.25 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end');
    label.setAttribute('dominant-baseline', 'middle');
    label.setAttribute('class', 'mystats-style-axis');
    label.textContent = axis.getStr();
    svg.appendChild(label);
  });

  const leagueValues = axes.map((a) => Number(style.league[a.key] ?? 0));
  const mineValues = axes.map((a) => Number(style.mine[a.key] ?? 0));

  const league = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  league.setAttribute('points', polygon(leagueValues));
  league.setAttribute('class', 'mystats-style-league');
  const mine = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  mine.setAttribute('points', polygon(mineValues));
  mine.setAttribute('class', 'mystats-style-mine');
  svg.append(league, mine);
  wrap.appendChild(svg);

  const legend = node('div', 'mystats-style-legend');
  legend.append(
    (() => {
      const item = node('span', 'mystats-style-legend-item');
      item.append(node('span', 'mystats-style-swatch is-mine'), node('span', '', t('mystats.style_mine')));
      return item;
    })(),
    (() => {
      const item = node('span', 'mystats-style-legend-item');
      item.append(node('span', 'mystats-style-swatch is-league'), node('span', '', t('mystats.style_league')));
      return item;
    })(),
    node('span', 'mystats-style-votes', t('mystats.style_votes', { n: style.votes })),
  );
  wrap.appendChild(legend);
  return wrap;
}

/** Краткое распределение типажей по всей лиге. */
function buildArchetypeDistribution(
  dist: MyStatsResponse['archetype_distribution'],
  mine: string | null,
): HTMLElement {
  if (!dist || dist.total === 0 || dist.items.length === 0) {
    return node('p', 'hint', t('mystats.no_data'));
  }

  const wrap = node('div', 'mystats-arch-dist');
  const max = Math.max(...dist.items.map((item) => item.count), 1);

  const makeRow = (item: ArchetypeDistItem) => {
    const isMine = mine != null && item.id === mine;
    const isEmpty = item.count === 0;
    const row = node(
      'div',
      `mystats-arch-dist-row${isMine ? ' is-mine' : ''}${isEmpty ? ' is-empty' : ''}`,
    );

    const head = node('div', 'mystats-arch-dist-head');
    const label = node('span', 'mystats-arch-dist-label');
    label.append(
      node('span', 'mystats-arch-dist-icon', ARCHETYPE_ICONS[item.id] ?? '🎭'),
      node('span', 'mystats-arch-dist-name', t(`mystats.arch.${item.id}.name` as TranslationKey)),
    );
    if (isMine) label.appendChild(node('span', 'mystats-arch-dist-you', t('mystats.arch_dist_you')));
    head.append(
      label,
      node(
        'span',
        'mystats-arch-dist-value',
        isEmpty ? '—' : `${item.count} · ${item.percent}%`,
      ),
    );

    const track = node('div', 'mystats-arch-dist-track');
    const fill = node('div', 'mystats-arch-dist-fill');
    fill.style.width = isEmpty ? '0%' : `${Math.max(2, (item.count / max) * 100)}%`;
    if (isMine) fill.classList.add('is-mine');
    track.appendChild(fill);

    row.append(head, track);
    return row;
  };

  // Кратко: топ-8, но свой типаж показываем всегда — даже если его нет
  // в распределении (например, у игрока меньше оценок, чем нужно для пула).
  const TOP_VISIBLE = 8;
  const top = dist.items.slice(0, TOP_VISIBLE);
  if (mine && !top.some((item) => item.id === mine)) {
    top.push(
      dist.items.find((item) => item.id === mine) ?? {
        id: mine,
        count: 0,
        percent: 0,
      },
    );
  }
  const rest = dist.items.filter(
    (item) => !top.some((visible) => visible.id === item.id),
  );

  const list = node('div', 'mystats-arch-dist-list');
  for (const item of top) list.appendChild(makeRow(item));
  wrap.appendChild(list);

  if (rest.length > 0) {
    const extra = node('div', 'mystats-arch-dist-list');
    extra.style.display = 'none';
    for (const item of rest) extra.appendChild(makeRow(item));
    wrap.appendChild(extra);

    const toggle = node(
      'button',
      'mystats-arch-dist-toggle',
      t('mystats.arch_dist_more', { n: rest.length }),
    );
    toggle.type = 'button';
    let open = false;
    toggle.addEventListener('click', () => {
      open = !open;
      extra.style.display = open ? '' : 'none';
      toggle.textContent = open
        ? t('mystats.arch_dist_less')
        : t('mystats.arch_dist_more', { n: rest.length });
    });
    wrap.appendChild(toggle);
  }

  wrap.appendChild(
    node(
      'p',
      'hint mystats-arch-dist-total',
      t('mystats.arch_dist_total', { n: dist.total }),
    ),
  );
  return wrap;
}

/** Выживаемость и длительность побед. */
function buildSurvival(s: MyStatsResponse['survival']): HTMLElement {
  const wrap = node('div', 'mystats-survival');
  const tiles = node('div', 'mystats-tiles mystats-tiles--3');
  tiles.append(
    buildStatTile(
      t('mystats.survival_share'),
      s.avg_share != null ? `${s.avg_share}%` : '—',
    ),
    buildStatTile(
      t('mystats.survival_top2'),
      s.top2_rate != null ? `${s.top2_rate}%` : '—',
    ),
    buildStatTile(
      t('mystats.survival_avg_win'),
      s.avg_win_min != null ? `${s.avg_win_min} ${t('mystats.min')}` : '—',
    ),
  );
  wrap.appendChild(tiles);

  const list = node('div', 'mystats-bars mystats-duration');
  const max = Math.max(...s.durations.map((d) => d.count), 1);
  for (const d of s.durations) {
    const line = node('div', 'mystats-bar');
    const head = node('div', 'mystats-bar-head');
    const label = d.key === '<10' ? '0–10' : d.key.replace('-', '–');
    head.append(
      node('span', 'mystats-bar-name', `${label} ${t('mystats.min')}`),
      node('span', 'mystats-bar-value', String(d.count)),
    );
    const track = node('div', 'mystats-bar-track');
    const fill = node('div', 'mystats-bar-fill');
    fill.style.width = `${Math.max(2, (d.count / max) * 100)}%`;
    track.appendChild(fill);
    line.append(head, track);
    list.appendChild(line);
  }
  wrap.appendChild(list);
  return wrap;
}

/** Личные счёты: лучшая «жертва», личный тренер и таблица соперников. */
function buildHeadToHead(data: MyStatsResponse): HTMLElement {
  const wrap = node('div', 'mystats-h2h');
  const edges = node('div', 'mystats-h2h-edges');
  const addEdge = (kind: 'best' | 'worst', row: BucketRow | null) => {
    if (!row) return;
    const card = node('div', `mystats-h2h-edge is-${kind}`);
    card.append(
      node(
        'span',
        'mystats-h2h-edge-label',
        t(kind === 'best' ? 'mystats.h2h_best' : 'mystats.h2h_worst'),
      ),
      node('span', 'mystats-h2h-edge-name', row.name),
      node('span', 'mystats-h2h-edge-stat', `${row.winrate}% · ${row.games}`),
    );
    card.addEventListener('click', () => navigateTo(`/?player=${encodeURIComponent(row.name)}`));
    edges.appendChild(card);
  };
  addEdge('best', data.rival_edges.best);
  addEdge('worst', data.rival_edges.worst);
  if (edges.childElementCount > 0) wrap.appendChild(edges);

  const list = node('div', 'mystats-h2h-list');
  for (const rival of data.rivals) {
    const row = node('div', 'mystats-h2h-row');
    const name = node('span', 'mystats-h2h-name', rival.name);
    const score = node('span', 'mystats-h2h-score', `${rival.wins}–${rival.games - rival.wins}`);
    const track = node('div', 'mystats-h2h-track');
    const fill = node('div', 'mystats-h2h-fill');
    fill.style.width = `${Math.max(2, rival.winrate)}%`;
    track.appendChild(fill);
    row.append(name, score, track, node('span', 'mystats-h2h-rate', `${rival.winrate}%`));
    row.classList.add('is-clickable');
    row.addEventListener('click', () => navigateTo(`/?player=${encodeURIComponent(rival.name)}`));
    list.appendChild(row);
  }
  if (data.rivals.length === 0) list.appendChild(node('p', 'hint', t('mystats.no_data')));
  wrap.appendChild(list);
  return wrap;
}

/** Итоги текущего месяца — «wrapped» в миниатюре. */
function buildWrapped(w: MyStatsResponse['wrapped']): HTMLElement {
  if (!w) return node('p', 'hint', t('mystats.wrapped_empty'));
  const wrap = node('div', 'mystats-wrapped');
  const monthLabel = new Date(`${w.month}-01T12:00:00`).toLocaleDateString(
    getLocale() === 'ru' ? 'ru-RU' : 'en-GB',
    { month: 'long', year: 'numeric' },
  );
  const head = node('div', 'mystats-wrapped-head');
  head.append(
    node('span', 'mystats-wrapped-month', monthLabel),
    node(
      'span',
      'mystats-wrapped-record',
      `${w.games} · ${w.wins} W · ${w.winrate}%`,
    ),
  );
  wrap.appendChild(head);

  const item = (label: string, value: string, extra?: string): HTMLElement => {
    const el = node('div', 'mystats-wrapped-item');
    el.append(
      node('span', 'mystats-wrapped-label', label),
      node('span', 'mystats-wrapped-value', value),
    );
    if (extra) el.appendChild(node('span', 'mystats-wrapped-extra', extra));
    return el;
  };
  const grid = node('div', 'mystats-wrapped-grid');
  grid.append(
    item(
      t('mystats.wrapped_elo'),
      `${w.elo_delta >= 0 ? '+' : ''}${w.elo_delta}`,
      `${w.active_days} ${t('mystats.records_days')}`,
    ),
    item(t('mystats.wrapped_map'), w.favorite_map ?? '—'),
    item(t('mystats.wrapped_rival'), w.top_rival ?? '—'),
    item(
      t('mystats.wrapped_best_day'),
      w.best_day ? fmtDateKey(w.best_day.date) : '—',
      w.best_day ? `${w.best_day.count} ${t('mystats.records_games')}` : undefined,
    ),
    item(
      t('mystats.wrapped_fastest'),
      w.fastest_win != null ? `${Math.round(w.fastest_win)} ${t('mystats.min')}` : '—',
    ),
    item(
      t('mystats.wrapped_longest'),
      w.longest_win != null ? `${Math.round(w.longest_win)} ${t('mystats.min')}` : '—',
    ),
  );
  wrap.appendChild(grid);
  return wrap;
}

/** Рекорды: перерыв, самый активный день и серия на карте. */
function buildRecords(
  records: MyStatsResponse['records'],
  bestStreak: number,
): HTMLElement {
  const grid = node('div', 'mystats-records');
  const item = (label: string, value: string, extra?: string): HTMLElement => {
    const el = node('div', 'mystats-record');
    el.append(node('span', 'mystats-record-label', label), node('span', 'mystats-record-value', value));
    if (extra) el.appendChild(node('span', 'mystats-record-extra', extra));
    return el;
  };
  const range =
    records.longest_break_from && records.longest_break_to
      ? `${fmtDate(records.longest_break_from)} – ${fmtDate(records.longest_break_to)}`
      : undefined;
  grid.append(
    item(
      t('mystats.records_break'),
      `${records.longest_break_days}`,
      range ? `${t('mystats.records_days')} · ${range}` : t('mystats.records_days'),
    ),
    item(
      t('mystats.records_max_day'),
      records.max_games_day ? `${records.max_games_day.count}` : '—',
      records.max_games_day
        ? `${t('mystats.records_games')} · ${fmtDateKey(records.max_games_day.date)}`
        : undefined,
    ),
    item(
      t('mystats.records_map_streak'),
      records.map_streak_count > 0 ? `${records.map_streak_count}` : '—',
      records.map_streak_name ?? undefined,
    ),
    item(
      t('mystats.records_best_series'),
      bestStreak > 0 ? `${bestStreak}` : '—',
      t('mystats.records_wins_in_row'),
    ),
  );
  return grid;
}

function buildPlaces(places: Array<{ place: number; count: number }>): HTMLElement {  const list = node('div', 'mystats-bars mystats-places');
  const max = Math.max(...places.map((p) => p.count), 1);
  for (const p of places) {
    const line = node('div', 'mystats-bar');
    const head = node('div', 'mystats-bar-head');
    head.append(
      node('span', 'mystats-bar-name', `#${p.place}`),
      node('span', 'mystats-bar-value', String(p.count)),
    );
    const track = node('div', 'mystats-bar-track');
    const fill = node('div', 'mystats-bar-fill');
    fill.style.width = `${Math.max(3, (p.count / max) * 100)}%`;
    if (p.place === 1) fill.classList.add('is-win');
    track.appendChild(fill);
    line.append(head, track);
    list.appendChild(line);
  }
  return list;
}

function buildTable(headers: string[], rows: string[][], link?: (i: number) => string): HTMLElement {
  const table = node('table', 'mystats-table');
  const thead = node('thead');
  const headRow = node('tr');
  for (const h of headers) headRow.appendChild(node('th', '', h));
  thead.appendChild(headRow);
  const tbody = node('tbody');
  rows.forEach((cells, i) => {
    const tr = node('tr');
    cells.forEach((cell, idx) => {
      const td = node('td', '', cell);
      td.dataset.label = headers[idx] ?? '';
      tr.appendChild(td);
    });
    if (link) {
      const href = link(i);
      if (href) {
        tr.classList.add('is-clickable');
        tr.addEventListener('click', () => navigateTo(href));
      }
    }
    tbody.appendChild(tr);
  });
  table.append(thead, tbody);
  return table;
}

/** Плашка админ-просмотра: видно, что открыта статистика другого игрока. */
function buildAdminBanner(playerName?: string): HTMLElement {
  const banner = node('div', 'mystats-admin-banner');
  banner.append(
    node('span', 'mystats-admin-banner-icon', '🛡️'),
    node(
      'span',
      'mystats-admin-banner-text',
      playerName ? t('mystats.admin_view', { name: playerName }) : t('mystats.admin_view_generic'),
    ),
  );
  const back = node('a', 'mystats-admin-banner-link', t('mystats.admin_back'));
  back.href = '/admin';
  banner.appendChild(back);
  return banner;
}

function buildRecentGames(data: MyStatsResponse): HTMLElement {
  const card = node('div', 'card mystats-card');
  const head = node('div', 'mystats-card-head');
  head.append(
    node('h3', '', t('mystats.recent_title')),
    (() => {
      const a = node('a', 'mystats-card-link', t('mystats.all_games'));
      a.href = `/games?player_id=${data.player.id}`;
      return a;
    })(),
  );
  card.appendChild(head);

  const list = node('div', 'mystats-recent');
  for (const game of data.recent) {
    const row = node('div', `mystats-recent-row ${game.won ? 'is-win' : 'is-loss'}`);
    const result = node('span', 'mystats-recent-result', game.won ? 'W' : 'L');
    const body = node('div', 'mystats-recent-body');
    const title = node('span', 'mystats-recent-map', game.map_name ?? '—');
    const meta = node('div', 'mystats-recent-meta');
    meta.append(
      node('span', '', fmtDate(game.played_at)),
      node(
        'span',
        '',
        t('mystats.recent_place', {
          place: game.place ?? '—',
          total: game.player_count,
        }),
      ),
      node('span', `mystats-race ${RACE_CLASS[game.race] ?? ''}`, game.race),
    );
    if (game.duration_min != null) {
      meta.appendChild(node('span', '', `${Math.round(Number(game.duration_min))} ${t('mystats.min')}`));
    }
    body.append(title, meta);
    const delta = node(
      'span',
      `mystats-recent-delta ${Number(game.elo_delta ?? 0) >= 0 ? 'is-plus' : 'is-minus'}`,
      game.elo_delta == null
        ? ''
        : `${Number(game.elo_delta) >= 0 ? '+' : ''}${Math.round(Number(game.elo_delta))}`,
    );
    row.append(result, body, delta);
    // Клик по строке открывает то же окно с игрой, что и в карточке игрока.
    row.classList.add('is-clickable');
    row.tabIndex = 0;
    row.setAttribute('role', 'button');
    const openGame = () => void openGameDetailModal(game.game_id);
    row.addEventListener('click', openGame);
    row.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      openGame();
    });
    list.appendChild(row);
  }
  if (data.recent.length === 0) list.appendChild(node('p', 'hint', t('mystats.no_data')));
  card.appendChild(list);
  return card;
}

function renderUnlinked(container: HTMLElement, adminView = false): void {
  container.innerHTML = '';
  if (adminView) container.appendChild(buildAdminBanner());
  const card = node('div', 'card mystats-empty');
  card.append(
    node('div', 'mystats-empty-icon', '📊'),
    node('h3', '', t('mystats.unlinked_title')),
    node('p', 'hint', t(adminView ? 'mystats.unlinked_admin' : 'mystats.unlinked')),
  );
  if (!adminView) {
    const profile = node('a', 'btn-primary', t('mystats.open_profile'));
    profile.href = '/profile';
    card.appendChild(profile);
  }
  container.appendChild(card);
}

function renderStats(container: HTMLElement, data: MyStatsResponse): void {
  container.innerHTML = '';
  const { summary, player } = data;

  // Админ смотрит чужую статистику — сверху плашка с возвратом.
  if (data.admin_view) container.appendChild(buildAdminBanner(player.name));

  // ---- Шапка: игрок, MMR, место, форма ----
  const hero = node('div', 'card mystats-hero');
  const identity = node('div', 'mystats-identity');
  identity.append(
    node('span', 'mystats-eyebrow', t('mystats.player')),
    node('h3', 'mystats-player-name', player.name),
  );
  if (player.aka) identity.appendChild(node('span', 'mystats-player-aka', player.aka));

  const eloBlock = node('div', 'mystats-elo-block');
  eloBlock.append(node('span', 'mystats-elo-value', fmtNumber(summary.elo)));
  eloBlock.appendChild(node('span', 'mystats-elo-label', t('mystats.mmr')));
  if (summary.rank != null) {
    eloBlock.appendChild(
      node(
        'span',
        'mystats-rank',
        t('mystats.rank_value', {
          rank: summary.rank,
          total: summary.total_players,
        }),
      ),
    );
  }
  identity.appendChild(eloBlock);

  const formWrap = node('div', 'mystats-form');
  formWrap.appendChild(node('span', 'mystats-form-label', t('mystats.recent_form')));
  const dots = node('div', 'mystats-form-dots');
  for (const item of data.form) {
    const dot = node(
      'span',
      `mystats-form-dot ${item.won ? 'is-win' : 'is-loss'}`,
      item.won ? 'W' : 'L',
    );
    dot.title = item.won ? t('mystats.form_win') : t('mystats.form_loss');
    dots.appendChild(dot);
  }
  if (data.form.length === 0) dots.appendChild(node('span', 'hint', t('mystats.no_data')));
  formWrap.appendChild(dots);
  identity.appendChild(formWrap);
  hero.appendChild(identity);

  const tiles = node('div', 'mystats-tiles');
  tiles.append(
    buildStatTile(t('mystats.games'), fmtNumber(summary.games), t('mystats.career')),
    buildStatTile(
      t('mystats.wins'),
      fmtNumber(summary.wins),
      `${summary.winrate}% ${t('mystats.winrate_short')}`,
    ),
    buildStatTile(t('mystats.avg_place'), summary.avg_place != null ? String(summary.avg_place) : '—'),
    buildStatTile(t('mystats.activity'), String(Math.round(summary.activity))),
    buildStatTile(t('mystats.game_days'), String(summary.game_days)),
    buildStatTile(
      t('mystats.elo_30d'),
      `${summary.elo_30d >= 0 ? '+' : ''}${summary.elo_30d}`,
      t('mystats.games_30d', { n: summary.games_30d }),
    ),
  );
  hero.appendChild(tiles);
  container.appendChild(hero);

  // ---- Челлендж недели ----
  if (lastChallenge) {
    container.appendChild(buildChallengeCard(lastChallenge, { compact: true }));
  }

  // ---- Типаж игрока ----
  if (data.archetype) {
    const archCard = node('div', 'card mystats-card mystats-card--archetype');
    archCard.append(
      node('h3', '', t('mystats.arch_title')),
      node('p', 'hint', t('mystats.arch_hint')),
      buildArchetypeBlock(data.archetype),
    );
    container.appendChild(archCard);
  }

  // ---- График MMR ----
  const chartCard = node('div', 'card mystats-card');
  chartCard.append(
    node('h3', '', t('mystats.elo_title')),
    node('p', 'hint', t('mystats.elo_hint', { n: summary.sample_games })),
    buildEloChart(data.elo_points),
  );
  container.appendChild(chartCard);

  // ---- Интересные факты и советы ----
  const factCard = node('div', 'card mystats-card mystats-card--facts');
  factCard.append(
    node('h3', '', t('mystats.facts_title')),
    node('p', 'hint', t('mystats.facts_hint')),
    buildNotes(data.facts, 'fact'),
  );
  container.appendChild(factCard);

  const adviceCard = node('div', 'card mystats-card mystats-card--advice');
  adviceCard.append(
    node('h3', '', t('mystats.advice_title')),
    node('p', 'hint', t('mystats.advice_hint')),
    buildNotes(data.advice, 'advice'),
  );
  container.appendChild(adviceCard);

  // ---- Хроника мест ----
  const timelineCard = node('div', 'card mystats-card');
  timelineCard.append(
    node('h3', '', t('mystats.timeline_title')),
    node('p', 'hint', t('mystats.timeline_hint')),
    buildPlaceTimeline(data.timeline),
  );
  container.appendChild(timelineCard);

  // ---- Перцентили в лиге ----
  const pctCard = node('div', 'card mystats-card');
  pctCard.append(
    node('h3', '', t('mystats.pct_title')),
    node('p', 'hint', t('mystats.pct_hint')),
    data.percentiles.length > 0
      ? buildPercentiles(data.percentiles)
      : node('p', 'hint', t('mystats.no_data')),
  );
  container.appendChild(pctCard);

  // ---- Итоги месяца ----
  const wrappedCard = node('div', 'card mystats-card');
  wrappedCard.append(
    node('h3', '', t('mystats.wrapped_title')),
    node('p', 'hint', t('mystats.wrapped_hint')),
    buildWrapped(data.wrapped),
  );
  container.appendChild(wrappedCard);

  // ---- Стиль против лиги ----
  const styleCard = node('div', 'card mystats-card');
  styleCard.append(
    node('h3', '', t('mystats.style_title')),
    node('p', 'hint', t('mystats.style_hint')),
    data.style
      ? buildStyleCompare(data.style)
      : node('p', 'hint', t('mystats.style_no_votes')),
  );
  container.appendChild(styleCard);

  // ---- Выживаемость и размер лобби ----
  const survivalSplit = node('div', 'mystats-split');
  const survivalCard = node('div', 'card mystats-card');
  survivalCard.append(
    node('h3', '', t('mystats.survival_title')),
    node('p', 'hint', t('mystats.survival_hint')),
    buildSurvival(data.survival),
  );
  const lobbyCard = node('div', 'card mystats-card');
  lobbyCard.append(
    node('h3', '', t('mystats.lobby_title')),
    node('p', 'hint', t('mystats.lobby_hint')),
    data.lobby_sizes.length > 0
      ? buildBars(data.lobby_sizes, false, (row) =>
          row.key === '6+'
            ? t('mystats.lobby_size_plus')
            : t('mystats.lobby_size', { n: row.key }),
        )
      : node('p', 'hint', t('mystats.no_data')),
  );
  survivalSplit.append(survivalCard, lobbyCard);
  container.appendChild(survivalSplit);

  // ---- Достижения ----
  const achCard = node('div', 'card mystats-card');
  achCard.append(
    node('h3', '', t('mystats.ach_title')),
    node('p', 'hint', t('mystats.ach_hint')),
    buildAchievements(data.achievements),
  );
  container.appendChild(achCard);

  // ---- Расы и распределение мест ----
  const split = node('div', 'mystats-split');
  const racesCard = node('div', 'card mystats-card');
  racesCard.append(
    node('h3', '', t('mystats.races_title')),
    buildBars(data.races, true),
  );
  const placesCard = node('div', 'card mystats-card');
  placesCard.append(
    node('h3', '', t('mystats.places_title')),
    buildPlaces(data.places),
  );
  split.append(racesCard, placesCard);
  container.appendChild(split);

  // ---- Карты ----
  const mapsCard = node('div', 'card mystats-card');
  mapsCard.append(
    node('h3', '', t('mystats.maps_title')),
    data.maps.length > 0
      ? buildTable(
          [
            t('mystats.map_col'),
            t('mystats.games_col'),
            t('mystats.winrate_col'),
            t('mystats.avg_place_col'),
          ],
          data.maps.map((m) => [
            m.name,
            String(m.games),
            `${m.winrate}%`,
            m.avg_place != null ? String(m.avg_place) : '—',
          ]),
        )
      : node('p', 'hint', t('mystats.no_data')),
  );
  container.appendChild(mapsCard);

  // ---- Личные счёты ----
  const rivalsCard = node('div', 'card mystats-card');
  rivalsCard.append(
    node('h3', '', t('mystats.h2h_title')),
    node('p', 'hint', t('mystats.h2h_hint')),
    buildHeadToHead(data),
  );
  container.appendChild(rivalsCard);

  // ---- Игровой ритм: компактная теплокарта день недели × час ----
  const rhythmCard = node('div', 'card mystats-card mystats-card--rhythm');
  rhythmCard.append(
    node('h3', '', t('mystats.rhythm_title')),
    node('p', 'hint', t('mystats.rhythm_hint')),
    buildHeatmap(data.heatmap),
  );
  container.appendChild(rhythmCard);

  // ---- Рекорды ----
  const recordsCard = node('div', 'card mystats-card');
  recordsCard.append(
    node('h3', '', t('mystats.records_title')),
    node('p', 'hint', t('mystats.records_hint', { n: summary.sample_games })),
    buildRecords(data.records, summary.best_streak),
  );
  container.appendChild(recordsCard);

  // ---- Последние игры ----
  container.appendChild(buildRecentGames(data));

  // ---- Типажи в лиге: краткое распределение по всем игрокам ----
  const distCard = node('div', 'card mystats-card');
  distCard.append(
    node('h3', '', t('mystats.arch_dist_title')),
    node('p', 'hint', t('mystats.arch_dist_hint')),
    buildArchetypeDistribution(
      data.archetype_distribution,
      data.archetype?.id ?? null,
    ),
  );
  container.appendChild(distCard);

  // Кнопка «Карточка игрока» в шапке страницы.
  const playerLink = document.getElementById('my-stats-player-link') as HTMLAnchorElement | null;
  if (playerLink) playerLink.href = `/?player=${encodeURIComponent(player.name)}`;

  // Заголовок: у чужого игрока показываем его имя, а не «Моя статистика».
  const titleEl = document.getElementById('my-stats-title');
  if (titleEl) {
    if (viewedPlayerId) {
      titleEl.removeAttribute('data-i18n');
      titleEl.textContent = t('mystats.player_title', { name: player.name });
    } else {
      titleEl.dataset.i18n = 'mystats.title';
      titleEl.textContent = t('mystats.title');
    }
  }
}

async function loadStats(): Promise<void> {
  const container = document.getElementById('my-stats-container');
  if (!container) return;
  if (!active) return;

  container.innerHTML = '<div class="skeleton skeleton-block"></div>';

  try {
    const url = viewedPlayerId
      ? `/api/me/stats?player_id=${encodeURIComponent(viewedPlayerId)}`
      : viewedUserId
        ? `/api/me/stats?user_id=${encodeURIComponent(viewedUserId)}`
        : '/api/me/stats';

    // Челлендж недели грузим параллельно со статистикой.
    const challengePromise = apiRequest<ChallengeResponse>('/api/challenge', {
      token: state.token,
    }).catch(() => null);

    const data = await apiRequest<MyStatsApiResponse>(url, {
      token: state.token,
    });
    if (!active) return;
    lastData = data;
    if (!data.linked) {
      renderUnlinked(container, Boolean(data.admin_view));
      return;
    }

    lastChallenge = await challengePromise;
    if (!active) return;

    renderStats(container, data);
  } catch (err) {
    console.error('[my-stats] load error:', err);
    if (!active) return;
    container.innerHTML = '';
    const card = node('div', 'card mystats-empty');
    card.append(
      node('div', 'mystats-empty-icon', '⚠️'),
      node('p', 'hint', t('mystats.error')),
    );
    const retry = node('button', 'btn-secondary', t('common.retry'));
    retry.type = 'button';
    retry.addEventListener('click', () => void loadStats());
    card.appendChild(retry);
    container.appendChild(card);
  }
}

export function mountMyStats(search?: URLSearchParams): void {
  const screen = document.getElementById('screen-my-stats');
  if (!screen) return;

  document.querySelectorAll<HTMLElement>('.screen').forEach((el) => {
    el.classList.toggle('hidden', el.id !== 'screen-my-stats');
  });

  // Чужую статистику открываем без авторизации: /my-stats?player_id=<id>.
  viewedPlayerId = search?.get('player_id')?.trim() || null;
  viewedUserId = search?.get('user_id')?.trim() || null;

  // Личная статистика только для авторизованных.
  if (!viewedPlayerId && (!state.user || !state.token)) {
    navigateTo('/auth', true);
    return;
  }

  active = true;

  const backBtn = document.getElementById('btn-back-my-stats');
  if (backBtn) {
    const fresh = backBtn.cloneNode(true) as HTMLElement;
    backBtn.replaceWith(fresh);
    fresh.addEventListener('click', () => {
      if (canGoBackInApp()) window.history.back();
      else navigateTo('/', true);
    });
  }

  // Смена языка — перерисовываем уже загруженные данные.
  if (localeListener) window.removeEventListener('i18n:changed', localeListener);
  localeListener = () => {
    const container = document.getElementById('my-stats-container');
    if (!container) return;
    if (lastData && lastData.linked) renderStats(container, lastData);
    else if (lastData) renderUnlinked(container, Boolean(lastData.admin_view));
  };
  window.addEventListener('i18n:changed', localeListener);

  lastData = null;
  lastChallenge = null;
  void loadStats();
}

export function unmountMyStats(): void {
  active = false;
  if (localeListener) {
    window.removeEventListener('i18n:changed', localeListener);
    localeListener = null;
  }
}
